import { apiDelete, apiGet, apiPatch, apiPost } from '../client'

export type SubscriptionTier = 'FREE' | 'BASIC' | 'PRO' | 'STARTER' | 'GROWTH' | 'ENTERPRISE'
export type BillingInterval = 'MONTHLY' | 'YEARLY' | 'QUARTERLY'
export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'TRIALING' | 'PAUSED' | 'EXPIRED'

export interface Plan {
  features: string[]
  id: string
  isActive: boolean
  name: string
  priceMonthly: number | null
  priceQuarterly: number | null
  priceYearly: number | null
  stripePriceIdMonthly: string | null
  stripePriceIdYearly: string | null
  tier: SubscriptionTier
}

export interface Subscription {
  billingInterval: BillingInterval
  cancelAtPeriodEnd: boolean
  cancelledAt: string | null
  cancelledReason: string | null
  currentPeriodEnd: string | null
  currentPeriodStart: string | null
  gracePeriodEnd: string | null
  id: string
  pendingBillingInterval: BillingInterval | null
  /** Set when a downgrade to a lower paid tier is scheduled - `plan` stays
   * on the current (higher) tier until `currentPeriodEnd`, at which point
   * the backend cron swaps it to `pendingPlan`. Downgrading to Free instead
   * shows up as `cancelAtPeriodEnd: true` with no `pendingPlan`. */
  pendingPlan: Plan | null
  pendingPlanId: string | null
  plan: Plan
  planId: string
  status: SubscriptionStatus
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  trialEndsAt: string | null
  userId: string
}

export interface UsageLimit {
  limit: number | null
  remaining: number | null
  used: number
}

export interface SubscriptionLimits {
  currentPeriodEnd: string
  currentPeriodStart: string
  features: {
    [key: string]: unknown
    employerMessaging: boolean
  }
  limits: {
    [key: string]: UsageLimit | undefined
    activePostings?: UsageLimit
    aiCareerCoaching?: UsageLimit
    aiMockInterviews?: UsageLimit
    aiProfileReviews?: UsageLimit
    eventRsvps?: UsageLimit
    jobApplications?: UsageLimit
    jobPostings?: UsageLimit
    savedJobs?: UsageLimit
    teamSeats?: UsageLimit
  }
  planName: string
  role: string
  status: string
  tier: string
}

export interface CheckoutSession {
  sessionId: string
  url: string
}

export interface CreateCheckoutPayload {
  billingInterval: BillingInterval
  cancelUrl?: string
  successUrl?: string
  tier: SubscriptionTier
}

export interface UpdateSubscriptionPayload {
  billingInterval?: BillingInterval
  cancelUrl?: string
  successUrl?: string
  tier?: SubscriptionTier
}

// PATCH /subscriptions/me re-routes through checkout (returns a CheckoutSession) the
// first time a Free user picks a paid tier; every other call returns the updated
// Subscription directly. Callers must check `'url' in result`.
export type UpdateSubscriptionResult = CheckoutSession | Subscription

export interface CancelSubscriptionPayload {
  immediately?: boolean
  reason?: string
}

export interface BillingPortalSession {
  url: string
}

export const getPlans = () => apiGet<Plan[]>('/subscriptions/plans')

export const getMySubscription = () => apiGet<Subscription>('/subscriptions/me')

export const getSubscriptionLimits = () => apiGet<SubscriptionLimits>('/subscriptions/limits')

export const createCheckoutSession = (payload: CreateCheckoutPayload) => apiPost<CheckoutSession>('/subscriptions/checkout', payload)

export const updateSubscription = (payload: UpdateSubscriptionPayload) => apiPatch<UpdateSubscriptionResult>('/subscriptions/me', payload)

export const cancelSubscription = (payload: CancelSubscriptionPayload) => apiDelete<Subscription>('/subscriptions/me', { data: payload })

export const reactivateSubscription = () => apiPost<Subscription>('/subscriptions/me/reactivate')

export const getBillingPortalUrl = (returnUrl: string) => apiPost<BillingPortalSession>('/subscriptions/billing-portal', { returnUrl })

export const resyncSubscription = () => apiPost<Subscription>('/subscriptions/me/resync')
